import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, Download, BadgeCheck, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, Overline } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CATEGORIES, APPLICATIONS } from "@/data/products";
import { useCatalog } from "@/lib/catalog";
import { DLC, FAQS, SITE } from "@/data/content";
import { track } from "@/lib/api";

const DOC_TYPES = ["Specification Sheet", "Wiring Diagram", "Sensor Coverage", "Dimensions"];

export default function Resources() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [app, setApp] = useState("All");
  const [docType, setDocType] = useState("All");
  const { products } = useCatalog();

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const q = query.toLowerCase();
      const matchQ =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.orderNumber.includes(q) ||
        (p.sensor || "").toLowerCase().includes(q) ||
        p.features.some((f) => f.toLowerCase().includes(q));
      const matchC = category === "All" || p.category === category;
      const matchA = app === "All" || p.applications.includes(app);
      return matchQ && matchC && matchA;
    });
  }, [query, category, app, products]);

  return (
    <div className="bg-bone min-h-screen" data-testid="resources-page">
      <SEO
        title="Resources — Datasheets, Specifications & Compliance"
        description="Amaya Pro resource library: product datasheets, wiring diagrams, sensor coverage, dimensions, DLC QPL listings and technical FAQ."
        path="/resources"
      />
      <PageHero
        overline="Resource Library"
        title="Specifications, documentation and compliance."
        sub="Every product, every spec — searchable and filterable. Individual PDF datasheets are being prepared; request any document and we'll send it."
      />

      {/* SEARCH + FILTERS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <Reveal>
          <div className="bg-white border border-zinc-200 p-5 md:p-6" data-testid="resource-search-panel">
            <div className="flex flex-col md:flex-row gap-4">
              <label className="flex-1 flex items-center gap-3 border border-zinc-200 bg-bone px-4 py-3">
                <Search size={16} className="text-zinc-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); track("resource_search", { q: e.target.value }); }}
                  placeholder="Search products, specifications and resources"
                  className="bg-transparent outline-none w-full text-sm text-ink placeholder:text-zinc-400"
                  data-testid="resource-search-input"
                />
              </label>
              <div className="flex flex-wrap gap-3">
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="border border-zinc-200 bg-bone px-3 py-2.5 text-sm text-ink" data-testid="filter-doc-category" aria-label="Filter by category">
                  <option value="All">All categories</option>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <select value={app} onChange={(e) => setApp(e.target.value)} className="border border-zinc-200 bg-bone px-3 py-2.5 text-sm text-ink" data-testid="filter-doc-app" aria-label="Filter by application">
                  <option value="All">All applications</option>
                  {APPLICATIONS.map((a) => <option key={a}>{a}</option>)}
                </select>
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className="border border-zinc-200 bg-bone px-3 py-2.5 text-sm text-ink" data-testid="filter-doc-type" aria-label="Filter by document type">
                  <option value="All">All document types</option>
                  {DOC_TYPES.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="font-mono text-xs text-zinc-500 mt-8 mb-4" data-testid="resource-count">
            {filtered.length} products · {docType === "All" ? "all document types" : docType}
          </p>
          <div className="bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="resource-list">
            {filtered.map((p) => (
              <div key={p.id} className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 px-5 py-4 hover:bg-bone transition-colors">
                <img src={p.image} alt="" loading="lazy" className="w-12 h-12 object-contain shrink-0 hidden md:block" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{p.name}</p>
                  <p className="font-mono text-[11px] text-zinc-500 mt-0.5">
                    № {p.orderNumber} · {p.category}{p.sensor ? ` · ${p.sensor}` : ""} · {p.applications.join(", ")}
                  </p>
                </div>
                <span className="text-[11px] px-2 py-1 bg-bone border border-zinc-200 text-zinc-500 w-max">
                  {docType === "All" ? "4 documents" : docType}
                </span>
                <div className="flex gap-4 shrink-0">
                  <Link to={`/products/${p.id}`} className="text-xs font-medium text-ink hover:text-signal transition-colors" data-testid={`resource-view-${p.id}`}>
                    View Specs
                  </Link>
                  <Link
                    to={`/contact?type=quote&product=${p.id}`}
                    onClick={() => track("datasheet_request", { product: p.id, doc: docType })}
                    className="text-xs font-medium text-signal flex items-center gap-1 hover:underline"
                    data-testid={`resource-download-${p.id}`}
                  >
                    <Download size={12} /> Request {docType === "All" ? "Datasheet" : docType}
                  </Link>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="resource-empty">No resources match your search.</p>
            )}
          </div>
        </Reveal>
      </section>

      {/* COMPLIANCE */}
      <section className="bg-forest py-24 text-white" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Certifications & Compliance</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">
              Listed. Verified. On the record.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {DLC.map((d) => (
              <Reveal key={d.productId}>
                <div className="border border-white/15 bg-white/5 p-8" data-testid={`dlc-${d.scope.toLowerCase().split(" ")[0]}`}>
                  <BadgeCheck size={24} className="text-signal" />
                  <h3 className="font-display text-xl font-bold mt-4">DesignLights Consortium — {d.scope}</h3>
                  <dl className="mt-5 space-y-2.5 text-sm">
                    {[["QPL", d.qpl], ["Product ID", d.productId], ["Listing Status", d.status], ["Date Qualified", d.qualified]].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 border-b border-white/15 pb-2">
                        <dt className="text-white/60">{k}</dt>
                        <dd className="text-white font-mono text-xs pt-0.5">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="border border-white/15 bg-white/5 p-8 mt-6 flex items-start gap-4">
              <ShieldCheck size={24} className="text-signal shrink-0" />
              <div>
                <h3 className="font-display text-xl font-bold">Security & warranty</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed max-w-2xl">
                  PSA Level 3 security is specified across Amaya Pro devices. The 06934 Power Pack Node is UL Listed (Category PAZX, Management Equipment — Energy) and UL 2043 Plenum Rated. Every product carries a 10-year in-house warranty with quick support and replacements.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="faq-section">
        <Reveal>
          <Overline dark={false}>FAQ</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink leading-[1.05] max-w-2xl">
            Straight answers.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12 bg-white border border-zinc-200">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-zinc-200 px-5">
                <AccordionTrigger className="text-left text-sm md:text-base font-medium text-ink hover:text-signal py-5" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-600 leading-relaxed pb-5" data-testid={`faq-a-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <CTASection title="Can't find what you need?" sub="Our team will send any specification, diagram or document directly." />
    </div>
  );
}
